import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppInterface } from '../../interfaces/app.interface';
import { catchError, Observable, of, shareReplay, take, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppFinderDataService } from '../appfinder-service/app-finder-data.service';
import { ErrorSignalService } from '../../components/error-page/services/error-signal.service';

@Injectable({
  providedIn: 'root',
})
export class AppInstanceAPIService {
  private readonly appInstanceCollectionEndpoint = '/me/appinstances-collection';
  private readonly appInstanceEndpoint = '/me/appinstances';
  private readonly rearrangeGridLayoutEndpoint = '/position';

  // Performance optimization: Request deduplication for concurrent requests
  constructor(
    private readonly http: HttpClient,
    private readonly appFinderDataService: AppFinderDataService,
    private readonly errorSignalService: ErrorSignalService,
  ) {}

  /**
   * This will take apps as parameter and send it to the backend.
   * The backend then creates an appInstance for the respective app.
   * It will then return list of a new app-object which includes an appInstanceId.
   * @param _apps
   * @param _searchFreeSpace
   */
  addAppInstances(_apps: AppInterface[], _searchFreeSpace = true): Observable<AppInterface[]> {
    return this.http
      .put<
        AppInterface[]
      >(environment.baseUrl + this.appInstanceCollectionEndpoint + '?searchFreeSpace=' + _searchFreeSpace, _apps)
      .pipe(
        take(1),
        tap((_saveAppInstance) => {
          for (const app of _saveAppInstance) {
            app.inDashboard = true;
          }
          this.appFinderDataService.fetchAllApps().subscribe();
        }),
        catchError((error) => {
          console.error('Error adding app instances:', error);
          return of([]);
        }),
      );
  }

  /**
   * This will take an app as parameter and send it to the backend.
   * The backend then creates and appInstance for the respective app.
   * It will then return a new app-object which includes an appInstanceId.
   * @param _app
   * @param options.propagateErrors When true, errors are propagated to the caller instead of being swallowed.
   */
  addAppInstance(
    _app: AppInterface,
    options?: { propagateErrors?: boolean },
  ): Observable<AppInterface> {
    const base$ = this.http
      .put<AppInterface>(environment.baseUrl + this.appInstanceEndpoint, _app)
      .pipe(
        take(1),
        tap((_saveAppInstance) => {
          _app.inDashboard = true;
          _app.appInstanceId = _saveAppInstance.appInstanceId;
          _app.folderId = _saveAppInstance.folderId;
        }),
      );
    return options?.propagateErrors
      ? base$
      : base$.pipe(
          catchError((error) => {
            console.error('Error adding app instance:', error);
            return of(_app);
          }),
        );
  }

  /**
   * DELETE: delete the portal app from the server.
   * @param options.propagateErrors When true, errors are propagated to the caller instead of being swallowed.
   */
  deleteAppInstance(
    app: AppInterface,
    options?: { propagateErrors?: boolean },
  ): Observable<void> {
    const base$ = this.http.delete<void>(
      environment.baseUrl + this.appInstanceEndpoint + '/' + app.appInstanceId,
    ).pipe(take(1));
    return options?.propagateErrors
      ? base$
      : base$.pipe(
          catchError((error) => {
            console.error('Error deleting app instance:', error);
            return of(void 0);
          }),
        );
  }

  updatePortalAppToFolder(_portalApp: AppInterface): Observable<AppInterface> {
    return this.http
      .post<AppInterface>(environment.baseUrl + this.appInstanceEndpoint, _portalApp)
      .pipe(
        take(1),
        tap(() => {
          this.appFinderDataService.fetchAllApps().subscribe();
        }),
        catchError((error) => {
          console.error('Error updating portal app to folder:', error);
          return of(_portalApp);
        }),
      );
  }

  getAppInstances(): Observable<AppInterface[]> {
    return this.http.get<AppInterface[]>(environment.baseUrl + this.appInstanceEndpoint).pipe(
      shareReplay(1), // Performance optimization: Cache for 1 subscriber
      catchError((error) => {
        console.error('Error getting app instances:', error);
        return of([]);
      }),
    );
  }

  rearrangeGridLayout(appInstanceIds: string[]): Observable<void> {
    // Convert string[] to number[] for Backend (List<Long>)
    const idsAsNumbers = appInstanceIds.map((id) => parseInt(id, 10));

    return this.http
      .post<void>(
        environment.baseUrl + this.appInstanceEndpoint + this.rearrangeGridLayoutEndpoint,
        idsAsNumbers,
      )
      .pipe(
        take(1),
        catchError((error) => {
          console.error('Error rearranging grid layout:', error);
          return of(void 0);
        }),
      );
  }
}
